import { normalizeVirtualPath } from '../../../../packages/core/src/virtual-path';
import { rawRequest, uploadRequest } from './client';
import type { NetworkBan } from '@mcpanel/core';
import type {
  DiskStatus,
  FileContent,
  FileListing,
  FileRootInfo,
  PlayerAction,
  PlayersView,
  ScheduledTask,
  ScheduledTaskInput,
  ServerPolicy,
  TaskRunResult,
} from './types-maint';

const id = (value: string) => encodeURIComponent(value);
const server = (serverId: string) => `/servers/${id(serverId)}`;

export const apiM = {
  /** 管理者を自動で OP・許可リストに入れるか（サーバーごと、既定は入れる） */
  adminAuto: (serverId: string) => rawRequest<{ enabled: boolean }>('GET', `${server(serverId)}/admin-auto`),
  setAdminAuto: (serverId: string, enabled: boolean) =>
    rawRequest<{ enabled: boolean }>('PUT', `${server(serverId)}/admin-auto`, { enabled }),
  storage: (serverId: string) => rawRequest<DiskStatus>('GET', `${server(serverId)}/storage`),

  tasks: (serverId: string) => rawRequest<ScheduledTask[]>('GET', `${server(serverId)}/tasks`),
  createTask: (serverId: string, input: ScheduledTaskInput) =>
    rawRequest<ScheduledTask>('POST', `${server(serverId)}/tasks`, input),
  updateTask: (serverId: string, taskId: string, input: ScheduledTaskInput) =>
    rawRequest<ScheduledTask>('PUT', `${server(serverId)}/tasks/${id(taskId)}`, input),
  deleteTask: (serverId: string, taskId: string) =>
    rawRequest<void>('DELETE', `${server(serverId)}/tasks/${id(taskId)}`),
  runTask: (serverId: string, taskId: string) =>
    rawRequest<{ result: TaskRunResult }>('POST', `${server(serverId)}/tasks/${id(taskId)}/run`),
  policy: (serverId: string) => rawRequest<ServerPolicy>('GET', `${server(serverId)}/policy`),
  setPolicy: (serverId: string, policy: ServerPolicy) =>
    rawRequest<ServerPolicy>('PUT', `${server(serverId)}/policy`, policy),

  players: (serverId: string) => rawRequest<PlayersView>('GET', `${server(serverId)}/players`),
  playerAction: (serverId: string, action: PlayerAction) =>
    rawRequest<{ output: string }>('POST', `${server(serverId)}/players/actions`, action),
  /** 全サーバー共通の IP BAN（edge で落とす） */
  networkBans: () => rawRequest<NetworkBan[]>('GET', '/network/ip-bans'),
  addNetworkBan: (address: string, reason?: string) =>
    rawRequest<NetworkBan[]>('POST', '/network/ip-bans', { address, reason }),
  removeNetworkBan: (address: string) => rawRequest<NetworkBan[]>('POST', '/network/ip-bans/remove', { address }),
  banPlayerIp: (serverId: string, player: string, reason?: string) =>
    rawRequest<{ address: string; bans: NetworkBan[] }>('POST', `${server(serverId)}/players/ip-ban`, {
      player,
      reason,
    }),

  fileRoots: (serverId: string) => rawRequest<FileRootInfo[]>('GET', `${server(serverId)}/files/roots`),
  listFiles: (serverId: string, path: string) =>
    rawRequest<FileListing>('GET', `${server(serverId)}/files?path=${encodeURIComponent(normalizeVirtualPath(path))}`),
  readFile: (serverId: string, path: string) =>
    rawRequest<FileContent>(
      'GET',
      `${server(serverId)}/files/content?path=${encodeURIComponent(normalizeVirtualPath(path))}`,
    ),
  writeFile: (serverId: string, path: string, content: string, expectedSha256?: string) =>
    rawRequest<{ sha256: string }>('PUT', `${server(serverId)}/files/content`, {
      path: normalizeVirtualPath(path),
      content,
      expectedSha256,
    }),
  makeDirectory: (serverId: string, path: string) =>
    rawRequest<{ ok: true }>('POST', `${server(serverId)}/files/mkdir`, { path: normalizeVirtualPath(path) }),
  trashFile: (serverId: string, path: string) =>
    rawRequest<{ trashedTo: string }>('POST', `${server(serverId)}/files/trash`, { path: normalizeVirtualPath(path) }),
  uploadFile: (serverId: string, path: string, file: Blob, overwrite = false) =>
    uploadRequest<{ path: string; size: number }>(
      `${server(serverId)}/files/upload?path=${encodeURIComponent(normalizeVirtualPath(path))}&overwrite=${overwrite}`,
      file,
    ),
};
